import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit2, Trash2, CalendarIcon, Filter, Search, ListTodo, CheckCircle, MoreVertical, LayoutGrid, List, FolderOpen, History, BarChart3, Eye, Clock, AlertCircle, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { format, isWithinInterval, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { Tables, Database } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';
import TaskTodoModal from './TaskTodoModal';
import ViewTaskModal from './ViewTaskModal';
import TasksKanbanView from './TasksKanbanView';
import TasksCalendarView from './TasksCalendarView';
import { getCurrentSingaporeDate } from '@/lib/dateUtils';
import { formatDuration } from '@/lib/timeUtils';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useProjects, ProjectWithDetails } from '@/hooks/useProjects';
import { useSchedulerState } from '@/hooks/useSchedulerState';
import ProjectsView from './ProjectsView';
import TaskStatusHistory from './TaskStatusHistory';
import TaskCompletionReport from './TaskCompletionReport';
import { useAuth } from '@/contexts/AuthContext';
import LoadingOverlay from './LoadingOverlay';
import DuplicateRoutineTasksModal from './DuplicateRoutineTasksModal';
import { toast } from 'sonner';

type StaffTodo = Tables<'staff_todos'> & { category?: string; project_id?: string };
type Project = Tables<'projects'>;

interface TasksViewProps {
  staff: Staff[];
  staffTodos: StaffTodo[];
  onAddTodo: (todo: Omit<StaffTodo, 'id' | 'created_at' | 'updated_at'>) => void;
  onUpdateTodo: (todo: StaffTodo) => void;
  onDeleteTodo: (id: string) => void;
  projects?: Project[];
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
}

type FilterType = 'all' | 'today' | 'week' | 'month' | 'custom';

const TasksView: React.FC<TasksViewProps> = ({
  staff,
  staffTodos,
  onAddTodo,
  onUpdateTodo,
  onDeleteTodo,
  projects: propProjects,
  isLoadingSchedules = false,
  isFetchingSchedules = false,
}) => {
  const { projects, createProject, updateProject, deleteProject, isLoading, error } = useProjects();
  const navigate = useNavigate();
  const { canDeleteTasks, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { allStaffForTasks } = useSchedulerState();
  const projectsToUse = propProjects || projects;
  const [isViewChanging, setIsViewChanging] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompleted, setShowCompleted] = useState(true);
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<StaffTodo | null>(null);
  const [viewType, setViewType] = useState<'list' | 'kanban' | 'calendar' | 'report'>('list');
  const [isProjectsModalOpen, setIsProjectsModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<{ id: string; title: string } | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTodo, setViewingTodo] = useState<StaffTodo | null>(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  // Use enhanced staff data for admin users, regular staff for others
  const activeStaff = isAdmin && allStaffForTasks.length > 0 
    ? allStaffForTasks.filter(s => s.status === 'active')
    : staff.filter(s => s.status === 'active');

  const filteredTodos = useMemo(() => {
    let filtered = staffTodos;

    // Filter by staff
    if (selectedStaffId !== 'all') {
      filtered = filtered.filter(todo => todo.staff_id === selectedStaffId);
    }

    // Filter by completion status
    if (!showCompleted) {
      filtered = filtered.filter(todo => todo.status !== 'completed');
    }

    // Filter by priority
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(todo => todo.priority === selectedPriority);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      if (selectedCategory === 'uncategorized') {
        filtered = filtered.filter(todo => !todo.category);
      } else {
        filtered = filtered.filter(todo => todo.category === selectedCategory);
      }
    }

    // Filter by project
    if (selectedProjectId !== 'all') {
      if (selectedProjectId === 'unassigned') {
        filtered = filtered.filter(todo => !todo.project_id);
      } else {
        filtered = filtered.filter(todo => todo.project_id === selectedProjectId);
      }
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(todo =>
        todo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (todo.description && todo.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by date - check both deadline and completed_at dates
    if (filterType !== 'all') {
      const now = getCurrentSingaporeDate();

      const isInDateRange = (todo: StaffTodo, start: Date, end: Date) => {
        // Check if deadline falls within range
        if (todo.deadline) {
          const deadlineDate = new Date(todo.deadline);
          if (isWithinInterval(deadlineDate, { start, end })) {
            return true;
          }
        }
        
        // Check if completed_at falls within range
        if (todo.completed_at) {
          const completedDate = new Date(todo.completed_at);
          if (isWithinInterval(completedDate, { start, end })) {
            return true;
          }
        }
        
        // Check if created_at falls within range (for tasks without deadline)
        const createdDate = new Date(todo.created_at);
        if (isWithinInterval(createdDate, { start, end })) {
          return true;
        }
        
        return false;
      };

      switch (filterType) {
        case 'today': {
          filtered = filtered.filter(todo =>
            isInDateRange(todo, startOfDay(now), endOfDay(now))
          );
          break;
        }
        case 'week': {
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - now.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          filtered = filtered.filter(todo =>
            isInDateRange(todo, startOfDay(weekStart), endOfDay(weekEnd))
          );
          break;
        }
        case 'month': {
          filtered = filtered.filter(todo =>
            isInDateRange(todo, startOfMonth(now), endOfMonth(now))
          );
          break;
        }
        case 'custom': {
          if (dateRange.from && dateRange.to) {
            filtered = filtered.filter(todo =>
              isInDateRange(todo, startOfDay(dateRange.from!), endOfDay(dateRange.to!))
            );
          }
          break;
        }
      }
    }

    // Sort tasks: completed tasks last, others by priority
    filtered.sort((a, b) => {
      const isACompleted = a.status === 'completed';
      const isBCompleted = b.status === 'completed';
      
      if (isACompleted !== isBCompleted) {
        return isACompleted ? 1 : -1; // Completed tasks go to bottom
      }
      
      // Within each group, sort by priority (high to low) then by created date (newest first)
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return filtered;
  }, [staffTodos, selectedStaffId, showCompleted, selectedPriority, selectedCategory, selectedProjectId, searchTerm, filterType, dateRange]);

  const groupedTodos = useMemo(() => {
    const grouped: Record<string, StaffTodo[]> = {};
    
    if (selectedStaffId === 'all') {
      activeStaff.forEach(staffMember => {
        grouped[staffMember.id] = filteredTodos.filter(todo => todo.staff_id === staffMember.id);
      });
    } else {
      grouped[selectedStaffId] = filteredTodos;
    }
    
    return grouped;
  }, [filteredTodos, activeStaff, selectedStaffId]);

  const handleAddTodo = () => {
    setEditingTodo(null);
    setIsModalOpen(true);
  };

  const handleDuplicateSuccess = () => {
    toast.success('Routine tasks duplicated successfully');
    // The modal will close itself, tasks will refresh automatically via React Query
  };

  const handleEditTodo = (todo: StaffTodo) => {
    setEditingTodo(todo);
    setIsModalOpen(true);
  };

  const handleViewTodo = (todo: StaffTodo) => {
    setViewingTodo(todo);
    setViewModalOpen(true);
  };

  const handleToggleComplete = (todo: StaffTodo) => {
    // This function is now deprecated as we use status dropdown
    // Keeping for backward compatibility
    onUpdateTodo(todo);
  };

  const handleShowHistory = (todo: StaffTodo) => {
    setSelectedTaskForHistory({ id: todo.id, title: todo.title });
    setHistoryModalOpen(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      case 'urgent':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStaffName = (staffId: string) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember?.name || 'Unknown';
  };

  const formatDuration = (minutes: number | null | undefined): string => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader className="px-3 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold">Staff Tasks</h2>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <ToggleGroup type="single" value={viewType} onValueChange={(value) => {
                if (value) {
                  setIsViewChanging(true);
                  setViewType(value as 'list' | 'kanban' | 'calendar' | 'report');
                  setTimeout(() => setIsViewChanging(false), 300);
                }
              }} className="w-full sm:w-auto">
                <ToggleGroupItem value="list" aria-label="List view">
                  <List className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="kanban" aria-label="Kanban view">
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="calendar" aria-label="Calendar view">
                  <CalendarIcon className="h-4 w-4" />
                </ToggleGroupItem>
                {isAdmin && (
                  <ToggleGroupItem value="report" aria-label="Report view">
                    <BarChart3 className="h-4 w-4" />
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
              <Button onClick={handleAddTodo} size="sm" className="flex-1 sm:flex-none">
                <Plus className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Add Task</span>
                <span className="sm:hidden">Add</span>
              </Button>
              <Button onClick={() => setIsProjectsModalOpen(true)} size="sm" variant="outline" className="flex-1 sm:flex-none">
                <FolderOpen className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Manage Projects</span>
              </Button>
              {/* Duplicate Routine Tasks - admin can select any staff, others can only duplicate their own */}
              {(isAdmin || user?.staffId) && (
                <Button onClick={() => setIsDuplicateModalOpen(true)} size="sm" variant="outline" className="flex-1 sm:flex-none">
                  <Copy className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Duplicate Routine Tasks</span>
                </Button>
              )}
              {isAdmin && (
                <Button onClick={() => navigate('/tasks/performance-review')} size="sm" variant="secondary" className="flex-1 sm:flex-none">
                  <BarChart3 className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Review Performance</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Staff Filter */}
            <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
              <SelectTrigger>
                <SelectValue placeholder="Select staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {isAdmin && allStaffForTasks.length > 0 ? (
                  // Admin view: Group staff by category
                  <>
                    {['Office Staff', 'Forum', 'Ngee Ann'].map(category => {
                      const categoryStaff = activeStaff.filter((s) => s.category === category);
                      if (categoryStaff.length === 0) return null;

                      return (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-medium text-muted-foreground bg-muted/50">
                            {category}
                          </div>
                          {categoryStaff.map((staffMember) => (
                            <SelectItem key={staffMember.id} value={staffMember.id} className="pl-4">
                              {staffMember.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  // Non-admin view: Simple list
                  activeStaff.map(staffMember => (
                    <SelectItem key={staffMember.id} value={staffMember.id}>
                      {staffMember.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            {/* Date Filter */}
            <Select value={filterType} onValueChange={(value: FilterType) => setFilterType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* Priority Filter */}
            <Select value={selectedPriority} onValueChange={setSelectedPriority}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="uncategorized">Uncategorized</SelectItem>
                <SelectItem value="General Admin">General Admin</SelectItem>
                <SelectItem value="Marketing">Marketing</SelectItem>
                <SelectItem value="HR & Staff Management">HR & Staff Management</SelectItem>
                <SelectItem value="Payroll – Staff">Payroll – Staff</SelectItem>
                <SelectItem value="Payroll – Doctor">Payroll – Doctor</SelectItem>
                <SelectItem value="Accounts">Accounts</SelectItem>
                <SelectItem value="Finance">Finance</SelectItem>
                <SelectItem value="Operations">Operations</SelectItem>
                <SelectItem value="Compliance">Compliance</SelectItem>
                <SelectItem value="Events – Internal">Events – Internal</SelectItem>
                <SelectItem value="Events – Education">Events – Education</SelectItem>
                <SelectItem value="Events – External">Events – External</SelectItem>
                <SelectItem value="Management Planning">Management Planning</SelectItem>
                <SelectItem value="Management Reports">Management Reports</SelectItem>
              </SelectContent>
            </Select>

          </div>

          {/* Second row of filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {/* Project Filter */}
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {projectsToUse?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search bar on its own row */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Additional Filters */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-completed"
                checked={showCompleted}
                onCheckedChange={(checked) => setShowCompleted(checked as boolean)}
              />
              <label
                htmlFor="show-completed"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Show completed tasks
              </label>
            </div>

            {filterType === 'custom' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {dateRange.from && dateRange.to
                      ? `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d')}`
                      : 'Select date range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => range?.from && range?.to && setDateRange({ from: range.from, to: range.to })}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tasks View */}
      {viewType === 'list' ? (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">
              {selectedStaffId === 'all' ? 'All Staff Tasks' : `${getStaffName(selectedStaffId)}'s Tasks`}
            </h3>
          </CardHeader>
          <CardContent>
            <TodoList
              todos={filteredTodos}
              onEdit={handleEditTodo}
              onView={handleViewTodo}
              onDelete={onDeleteTodo}
              onToggleComplete={handleToggleComplete}
              getPriorityColor={getPriorityColor}
              showStaffName={selectedStaffId === 'all'}
              getStaffName={getStaffName}
              onUpdateTodo={onUpdateTodo}
              projects={projectsToUse}
              onShowHistory={handleShowHistory}
              canDelete={canDeleteTasks()}
            />
          </CardContent>
        </Card>
      ) : viewType === 'kanban' ? (
        <TasksKanbanView
          todos={filteredTodos}
          staff={staff}
          onEdit={handleEditTodo}
          onDelete={onDeleteTodo}
          onUpdateTodo={onUpdateTodo}
          showStaffName={selectedStaffId === 'all'}
          projects={projectsToUse}
          canDelete={canDeleteTasks()}
        />
      ) : viewType === 'calendar' ? (
        <TasksCalendarView
          todos={filteredTodos}
          staff={staff}
          onEdit={handleEditTodo}
          onView={handleViewTodo}
          onDelete={onDeleteTodo}
          onUpdateTodo={onUpdateTodo}
          showStaffName={selectedStaffId === 'all'}
          projects={projectsToUse}
          getPriorityColor={getPriorityColor}
        />
      ) : viewType === 'report' && isAdmin ? (
        <TaskCompletionReport
          todos={filteredTodos}
          staff={staff}
          projects={projectsToUse}
        />
      ) : (
        // Fallback to list view if non-admin tries to access report
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">
              {selectedStaffId === 'all' ? 'All Staff Tasks' : `${getStaffName(selectedStaffId)}'s Tasks`}
            </h3>
          </CardHeader>
          <CardContent>
            <TodoList
              todos={filteredTodos}
              onEdit={handleEditTodo}
              onView={handleViewTodo}
              onDelete={onDeleteTodo}
              onToggleComplete={handleToggleComplete}
              getPriorityColor={getPriorityColor}
              showStaffName={selectedStaffId === 'all'}
              getStaffName={getStaffName}
              onUpdateTodo={onUpdateTodo}
              projects={projectsToUse}
              onShowHistory={handleShowHistory}
              canDelete={canDeleteTasks()}
            />
          </CardContent>
        </Card>
      )}

      {/* Task Modal */}
      <TaskTodoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(todo) => {
          if (editingTodo) {
            onUpdateTodo({ ...editingTodo, ...todo });
          } else {
            onAddTodo(todo);
          }
          setIsModalOpen(false);
        }}
        staff={activeStaff}
        todo={editingTodo}
        defaultStaffId={selectedStaffId !== 'all' ? selectedStaffId : undefined}
        projects={projectsToUse}
      />

      {/* Projects Management Modal */}
      <Dialog open={isProjectsModalOpen} onOpenChange={setIsProjectsModalOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage Projects</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            <ProjectsView
              projects={projectsToUse}
              staff={staff}
              onAddProject={createProject}
              onUpdateProject={updateProject}
              onDeleteProject={deleteProject}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Status History Modal */}
      {selectedTaskForHistory && (
        <TaskStatusHistory
          isOpen={historyModalOpen}
          onClose={() => {
            setHistoryModalOpen(false);
            setSelectedTaskForHistory(null);
          }}
          taskId={selectedTaskForHistory.id}
          taskTitle={selectedTaskForHistory.title}
        />
      )}

      {/* View Task Modal */}
      <ViewTaskModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingTodo(null);
        }}
        todo={viewingTodo}
        staff={staff}
        projects={projectsToUse}
      />

      <LoadingOverlay
        isLoading={isLoadingSchedules || isViewChanging}
        message={isViewChanging ? "Switching view..." : "Loading tasks..."}
      />

      {/* Duplicate Routine Tasks Modal */}
      <DuplicateRoutineTasksModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        onSuccess={handleDuplicateSuccess}
        staffId={user?.staffId}
        isAdmin={isAdmin}
      />
    </div>
  );
};

interface TodoListProps {
  todos: StaffTodo[];
  onEdit: (todo: StaffTodo) => void;
  onView: (todo: StaffTodo) => void;
  onDelete: (id: string) => void;
  onToggleComplete: (todo: StaffTodo) => void;
  getPriorityColor: (priority: string) => string;
  showStaffName?: boolean;
  getStaffName?: (staffId: string) => string;
  onUpdateTodo?: (todo: StaffTodo) => void;
  projects?: ProjectWithDetails[];
  onShowHistory?: (todo: StaffTodo) => void;
  canDelete?: boolean;
}

const TodoList: React.FC<TodoListProps> = ({
  todos,
  onEdit,
  onView,
  onDelete,
  onToggleComplete,
  getPriorityColor,
  showStaffName = false,
  getStaffName,
  onUpdateTodo,
  projects,
  onShowHistory,
  canDelete = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;
  const [lateReasonDialog, setLateReasonDialog] = useState<{
    show: boolean;
    task: StaffTodo | null;
    newStatus: string | null;
  }>({ show: false, task: null, newStatus: null });
  const [lateReason, setLateReason] = useState('');

  // Reset to page 1 when todos change
  useEffect(() => {
    setCurrentPage(1);
  }, [todos.length]);

  const handleStatusChange = (todo: StaffTodo, newStatus: string) => {
    const isBeingCompleted = newStatus === 'completed' && todo.status !== 'completed';

    if (isBeingCompleted && todo.deadline) {
      // Check if task is late using deadline + 1 day logic
      const deadlineDate = new Date(todo.deadline);
      const completionDate = new Date();
      const deadlinePlusOne = new Date(deadlineDate);
      deadlinePlusOne.setDate(deadlinePlusOne.getDate() + 1);
      deadlinePlusOne.setHours(0, 0, 0, 0);
      const completionDateNormalized = new Date(completionDate);
      completionDateNormalized.setHours(0, 0, 0, 0);
      const isLate = completionDateNormalized >= deadlinePlusOne;

      if (isLate) {
        setLateReasonDialog({ show: true, task: todo, newStatus });
        return;
      }
    }

    // Not late or not being completed, proceed with update
    const updatedTodo = {
      ...todo,
      status: newStatus as Database['public']['Enums']['task_status_new'],
      completed_at: newStatus === 'completed' ? new Date().toISOString() :
                   todo.status === 'completed' ? null : todo.completed_at,
    };
    if (onUpdateTodo) {
      onUpdateTodo(updatedTodo);
    } else {
      onToggleComplete(updatedTodo);
    }
  };

  const handleLateReasonSubmit = () => {
    if (!lateReason.trim()) {
      return;
    }

    if (lateReasonDialog.task && lateReasonDialog.newStatus) {
      const updatedTodo = {
        ...lateReasonDialog.task,
        status: lateReasonDialog.newStatus as Database['public']['Enums']['task_status_new'],
        completed_at: new Date().toISOString(),
        late_reason: lateReason.trim(),
      };
      if (onUpdateTodo) {
        onUpdateTodo(updatedTodo);
      } else {
        onToggleComplete(updatedTodo);
      }

      setLateReasonDialog({ show: false, task: null, newStatus: null });
      setLateReason('');
    }
  };

  if (todos.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <ListTodo className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p>No tasks found</p>
      </div>
    );
  }

  // Separate completed and pending todos for visual grouping
  const pendingTodos = todos.filter(todo => todo.status !== 'completed' && todo.status !== 'cancelled');
  const completedTodos = todos.filter(todo => todo.status === 'completed' || todo.status === 'cancelled');
  const showCompletedSection = completedTodos.length > 0;

  // Pagination logic
  const allTodos = [...pendingTodos, ...completedTodos];
  const totalItems = allTodos.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  // Slice todos for current page
  const paginatedAllTodos = allTodos.slice(startIndex, endIndex);
  const paginatedPendingTodos = paginatedAllTodos.filter(todo => todo.status !== 'completed' && todo.status !== 'cancelled');
  const paginatedCompletedTodos = paginatedAllTodos.filter(todo => todo.status === 'completed' || todo.status === 'cancelled');
  const showPaginatedCompletedSection = paginatedCompletedTodos.length > 0;

  return (
    <div className="space-y-4">
      {/* Pending Tasks */}
      <div className="space-y-2">
        {paginatedPendingTodos.map(todo => {
          // Check if task is overdue - compare dates only (ignore time)
          // Also determine if it's "critical" overdue (2+ extensions = red, otherwise orange)
          const { isOverdue, isCriticalOverdue } = (() => {
            if (!todo.deadline || todo.status === 'completed' || todo.status === 'cancelled') {
              return { isOverdue: false, isCriticalOverdue: false };
            }

            const deadlineDate = new Date(todo.deadline);
            const today = new Date();
            deadlineDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            const overdue = today > deadlineDate;
            // Critical overdue: 2+ extensions means red, otherwise orange
            const extensionCount = (todo as any).extension_count ?? 0;
            const critical = overdue && extensionCount >= 2;

            return { isOverdue: overdue, isCriticalOverdue: critical };
          })();
          
          return (
            <div
              key={todo.id}
              className={`flex flex-col gap-3 p-3 sm:p-4 border rounded-lg hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between ${
                isCriticalOverdue ? 'bg-red-100' : isOverdue ? 'bg-orange-100' : ''
              }`}
            >
          <div className="flex items-start space-x-3 w-full sm:flex-1">
            <div className="flex-1">
              <h4 className={`font-medium ${todo.status === 'completed' || todo.status === 'cancelled' ? 'line-through' : ''}`}>
                {todo.title}
              </h4>
              {todo.description && (
                <div 
                  className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1"
                  dangerouslySetInnerHTML={{ __html: todo.description }}
                />
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {showStaffName && getStaffName && (
                  <Badge variant="outline" className="text-xs">
                    {getStaffName(todo.staff_id)}
                  </Badge>
                )}
                <Badge className={getPriorityColor(todo.priority)}>
                  {todo.priority}
                </Badge>
                {todo.category && (
                  <Badge variant="secondary" className="text-xs">
                    {todo.category}
                  </Badge>
                )}
                {todo.task_type && (
                  <Badge variant="outline" className={`text-xs ${todo.task_type === 'Routine' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                    {todo.task_type}
                  </Badge>
                )}
                {todo.project_id && (
                  <Badge variant="outline" className="text-xs">
                    {projects?.find(p => p.id === todo.project_id)?.name || 'Project'}
                  </Badge>
                )}
                {todo.start_date && (
                  <span className="text-sm text-blue-600">
                    Start: {format(new Date(todo.start_date), 'MMM d, yyyy')}
                  </span>
                )}
                {todo.deadline && (
                  <span className={`text-sm ${
                    isCriticalOverdue ? 'text-red-600 font-medium' :
                    isOverdue ? 'text-orange-600 font-medium' : 'text-gray-500'
                  }`}>
                    Due: {format(new Date(todo.deadline), 'MMM d, yyyy')}
                    {isOverdue && ' (Overdue)'}
                  </span>
                )}
                {todo.status === 'completed' && todo.completed_at && (
                  <span className="text-sm text-green-600">
                    <CheckCircle className="h-3 w-3 inline mr-1" />
                    Completed: {format(new Date(todo.completed_at), 'MMM d, yyyy')}
                  </span>
                )}
                {todo.estimated_duration && (
                  <Badge variant="outline" className="text-xs flex items-center gap-1 bg-blue-50">
                    <Clock className="h-3 w-3 text-blue-600" />
                    Est: {formatDuration(todo.estimated_duration)}
                  </Badge>
                )}
                {todo.actual_duration && (
                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Actual: {formatDuration(todo.actual_duration)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
            <Select
              value={todo.status}
              onValueChange={(value) => handleStatusChange(todo, value)}
            >
              <SelectTrigger className={`h-8 w-full sm:w-[150px] ${
                todo.status === 'completed' ? 'text-green-600' : 
                todo.status === 'cancelled' ? 'text-red-600' :
                todo.status === 'in-progress' ? 'text-blue-600' :
                todo.status === 'review' ? 'text-purple-600' :
                todo.status === 'on-hold' ? 'text-orange-600' : ''
              }`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></div>
                    Pending
                  </span>
                </SelectItem>
                <SelectItem value="in-progress">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                    In Progress
                  </span>
                </SelectItem>
                <SelectItem value="review">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                    Review
                  </span>
                </SelectItem>
                <SelectItem value="on-hold">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mr-2"></div>
                    On Hold
                  </span>
                </SelectItem>
                <SelectItem value="completed">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                    Completed
                  </span>
                </SelectItem>
                <SelectItem value="cancelled">
                  <span className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                    Cancelled
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            {onShowHistory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onShowHistory(todo)}
                title="View status history"
              >
                <History className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onView(todo)}
              title="View task details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(todo)}
              title="Edit task"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(todo.id)}
                className="text-red-600 hover:text-red-700"
                title="Delete task"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        );
        })}
      </div>

      {/* Completed Tasks Section */}
      {showPaginatedCompletedSection && (
        <>
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Completed Tasks</h3>
          </div>
          <div className="space-y-2">
            {paginatedCompletedTodos.map(todo => (
              <div
                key={todo.id}
                className="flex flex-col gap-3 p-3 sm:p-4 border rounded-lg hover:bg-gray-50 opacity-60 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start space-x-3 w-full sm:flex-1">
                  <div className="flex-1">
                    <h4 className="font-medium line-through">
                      {todo.title}
                    </h4>
                    {todo.description && (
                      <div 
                        className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1"
                        dangerouslySetInnerHTML={{ __html: todo.description }}
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {showStaffName && getStaffName && (
                        <Badge variant="outline" className="text-xs">
                          {getStaffName(todo.staff_id)}
                        </Badge>
                      )}
                      <Badge className={getPriorityColor(todo.priority)}>
                        {todo.priority}
                      </Badge>
                      {todo.category && (
                        <Badge variant="secondary" className="text-xs">
                          {todo.category}
                        </Badge>
                      )}
                      {todo.project_id && (
                        <Badge variant="outline" className="text-xs">
                          {projects?.find(p => p.id === todo.project_id)?.name || 'Project'}
                        </Badge>
                      )}
                      {todo.start_date && (
                        <span className="text-sm text-blue-600">
                          Start: {format(new Date(todo.start_date), 'MMM d, yyyy')}
                        </span>
                      )}
                      {todo.deadline && (
                        <span className="text-sm text-gray-500">
                          Due: {format(new Date(todo.deadline), 'MMM d, yyyy')}
                        </span>
                      )}
                      {todo.status === 'completed' && todo.completed_at && (
                        <span className="text-sm text-green-600">
                          <CheckCircle className="h-3 w-3 inline mr-1" />
                          Completed: {format(new Date(todo.completed_at), 'MMM d, yyyy')}
                        </span>
                      )}
                      {todo.estimated_duration && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1 bg-blue-50">
                          <Clock className="h-3 w-3 text-blue-600" />
                          Est: {formatDuration(todo.estimated_duration)}
                        </Badge>
                      )}
                      {todo.actual_duration && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Actual: {formatDuration(todo.actual_duration)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap sm:justify-end">
                  <Select
                    value={todo.status}
                    onValueChange={(value) => handleStatusChange(todo, value)}
                  >
                    <SelectTrigger className={`h-8 w-full sm:w-[120px] ${
                      todo.status === 'completed' ? 'text-green-600' : 
                      todo.status === 'cancelled' ? 'text-red-600' :
                      todo.status === 'in-progress' ? 'text-blue-600' :
                      todo.status === 'review' ? 'text-purple-600' :
                      todo.status === 'on-hold' ? 'text-orange-600' : ''
                    }`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></div>
                          Pending
                        </span>
                      </SelectItem>
                      <SelectItem value="in-progress">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                          In Progress
                        </span>
                      </SelectItem>
                      <SelectItem value="review">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                          Review
                        </span>
                      </SelectItem>
                      <SelectItem value="on-hold">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-orange-500 mr-2"></div>
                          On Hold
                        </span>
                      </SelectItem>
                      <SelectItem value="completed">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                          Completed
                        </span>
                      </SelectItem>
                      <SelectItem value="cancelled">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                          Cancelled
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onView(todo)}
                    title="View task details"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(todo)}
                    title="Edit task"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(todo.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Delete task"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 border-t pt-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-500">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} tasks
            </p>
            <div className="overflow-x-auto pb-1">
              <Pagination>
                <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* First page */}
                {currentPage > 2 && (
                  <>
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setCurrentPage(1)}
                        className="cursor-pointer"
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                    {currentPage > 3 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                  </>
                )}

                {/* Current page and surrounding pages */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return page === currentPage ||
                           page === currentPage - 1 ||
                           page === currentPage + 1;
                  })
                  .map(page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                {/* Last page */}
                {currentPage < totalPages - 1 && (
                  <>
                    {currentPage < totalPages - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setCurrentPage(totalPages)}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  />
                </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </div>
        </div>
      )}

      {/* Late Reason Dialog */}
      <Dialog open={lateReasonDialog.show} onOpenChange={(open) => {
        if (!open) {
          setLateReasonDialog({ show: false, task: null, newStatus: null });
          setLateReason('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Task Completed Late</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="border-orange-500 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-900">
                This task is being completed after its deadline. Please provide a reason for the delay.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="late-reason">Reason for Late Completion *</Label>
              <Textarea
                id="late-reason"
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                placeholder="Explain why this task was completed late..."
                rows={4}
                maxLength={500}
              />
              <div className="text-xs text-muted-foreground text-right">
                {lateReason.length}/500 characters
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLateReasonDialog({ show: false, task: null, newStatus: null });
                setLateReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLateReasonSubmit}
              disabled={!lateReason.trim()}
            >
              Complete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksView;
